/**
 * MacOSDock - Classic Mac OS X style dock
 *
 * Features:
 * - Bottom-positioned dock with magnification on hover
 * - Classic aqua-style icons
 * - Bounce animation on click
 * - Reflection effect
 */
import { memo, useState, useCallback, useRef } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import type * as Y from 'yjs';
import type { Node } from '@xyflow/react';
import { STICKY_COLORS } from '../nodes';
import { createDefaultBlockData, createDefaultChatNodeData } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface MacOSDockProps {
  nodesMap: Y.Map<Node>;
  makeChange: (fn: () => void) => void;
}

interface DockItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const MacOSDock = memo(function MacOSDock({ nodesMap, makeChange }: MacOSDockProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [bouncingId, setBouncingId] = useState<string | null>(null);
  const spawnIndexRef = useRef(0);

  const { getViewport } = useReactFlow();
  const { viewportWidth, viewportHeight } = useStore((state) => ({
    viewportWidth: state.width,
    viewportHeight: state.height,
  }));

  // Get spawn position at center
  const getSpawnPosition = useCallback(() => {
    const viewport = getViewport();
    const index = spawnIndexRef.current;
    spawnIndexRef.current += 1;

    const centerX = (viewportWidth / 2 - viewport.x) / viewport.zoom;
    const centerY = (viewportHeight / 2 - viewport.y) / viewport.zoom;

    // Stagger positions slightly
    const offset = index * 20;
    return { x: centerX + offset, y: centerY + offset };
  }, [getViewport, viewportWidth, viewportHeight]);

  // Create node with bounce animation
  const createNode = useCallback((type: string, data: any, itemId: string) => {
    setBouncingId(itemId);
    setTimeout(() => setBouncingId(null), 500);

    const position = getSpawnPosition();
    const id = nanoid();

    const newNode: Node = {
      id,
      type,
      position,
      data,
    };

    makeChange(() => {
      nodesMap.set(id, newNode);
    });
  }, [getSpawnPosition, nodesMap, makeChange]);

  // Dock items
  const dockItems: DockItem[] = [
    {
      id: 'finder',
      label: 'Finder',
      color: '#1E90FF',
      icon: <FinderIcon />,
      onClick: () => createNode('stickyNote', { text: '', color: STICKY_COLORS.blue, width: 200, height: 150 }, 'finder'),
    },
    {
      id: 'notes',
      label: 'Notes',
      color: '#FFD60A',
      icon: <NotesIcon />,
      onClick: () => createNode('stickyNote', { text: '', color: STICKY_COLORS.yellow, width: 200, height: 150 }, 'notes'),
    },
    {
      id: 'textedit',
      label: 'TextEdit',
      color: '#FF6B6B',
      icon: <TextEditIcon />,
      onClick: () => createNode('block', createDefaultBlockData(), 'textedit'),
    },
    {
      id: 'messages',
      label: 'Messages',
      color: '#34C759',
      icon: <MessagesIcon />,
      onClick: () => createNode('chat', createDefaultChatNodeData(), 'messages'),
    },
    {
      id: 'ipod',
      label: 'iTunes',
      color: '#FF2D55',
      icon: <ITunesIcon />,
      onClick: () => createNode('ipod', { title: 'My iPod' }, 'ipod'),
    },
  ];

  // Calculate magnification based on distance from hovered item
  const getScale = (index: number) => {
    if (hoveredIndex === null) return 1;
    const distance = Math.abs(index - hoveredIndex);
    if (distance === 0) return 1.5;
    if (distance === 1) return 1.25;
    if (distance === 2) return 1.1;
    return 1;
  };

  return (
    <div className="macos-dock-container">
      <div className="macos-dock">
        {dockItems.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={`macos-dock-item ${bouncingId === item.id ? 'bouncing' : ''}`}
            style={{
              transform: `scale(${getScale(index)})`,
              transformOrigin: 'bottom',
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            onClick={item.onClick}
            title={item.label}
          >
            <div className="macos-dock-icon" style={{ background: item.color }}>
              {item.icon}
            </div>
            <div className="macos-dock-label">{item.label}</div>
          </button>
        ))}

        {/* Divider */}
        <div className="macos-dock-divider" />

        {/* Trash */}
        <button
          type="button"
          className="macos-dock-item"
          style={{
            transform: `scale(${hoveredIndex === dockItems.length ? 1.5 : 1})`,
            transformOrigin: 'bottom',
          }}
          onMouseEnter={() => setHoveredIndex(dockItems.length)}
          onMouseLeave={() => setHoveredIndex(null)}
          title="Trash"
        >
          <div className="macos-dock-icon" style={{ background: '#8E8E93' }}>
            <TrashIcon />
          </div>
          <div className="macos-dock-label">Trash</div>
        </button>
      </div>

      {/* Reflection */}
      <div className="macos-dock-reflection" />

      <style>{`
        .macos-dock-container {
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 100;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .macos-dock {
          display: flex;
          align-items: flex-end;
          gap: 2px;
          padding: 4px 8px 2px 8px;
          /* Lion-style glassy shelf dock */
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.2) 40%,
            rgba(200, 200, 220, 0.3) 60%,
            rgba(150, 150, 180, 0.4) 100%
          );
          border-radius: 6px;
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-bottom: 1px solid rgba(0, 0, 0, 0.2);
          box-shadow:
            0 0 0 1px rgba(0, 0, 0, 0.15),
            0 8px 32px rgba(0, 0, 0, 0.35),
            inset 0 1px 0 rgba(255, 255, 255, 0.5),
            inset 0 -2px 4px rgba(0, 0, 0, 0.1);
        }

        .macos-dock-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 4px;
          background: none;
          border: none;
          cursor: pointer;
          transition: transform 0.15s ease-out;
        }

        .macos-dock-item.bouncing {
          animation: dock-bounce 0.5s ease;
        }

        @keyframes dock-bounce {
          0%, 100% { transform: translateY(0) scale(1.5); }
          25% { transform: translateY(-20px) scale(1.5); }
          50% { transform: translateY(-10px) scale(1.5); }
          75% { transform: translateY(-15px) scale(1.5); }
        }

        .macos-dock-icon {
          width: 52px;
          height: 52px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          position: relative;
          /* Lion glossy aqua effect */
          box-shadow:
            0 3px 8px rgba(0, 0, 0, 0.4),
            0 6px 20px rgba(0, 0, 0, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
          transition: all 0.15s ease;
        }

        .macos-dock-icon::before {
          content: '';
          position: absolute;
          top: 2px;
          left: 4px;
          right: 4px;
          height: 45%;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.6) 0%,
            rgba(255, 255, 255, 0.1) 100%
          );
          border-radius: 8px 8px 50% 50%;
          pointer-events: none;
        }

        .macos-dock-item:hover .macos-dock-icon {
          box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.5),
            0 8px 30px rgba(0, 0, 0, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .macos-dock-icon svg {
          width: 30px;
          height: 30px;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
          position: relative;
          z-index: 1;
        }

        .macos-dock-label {
          font-size: 10px;
          font-weight: 500;
          color: #333;
          opacity: 0;
          transform: translateY(-4px);
          transition: all 0.15s ease;
          white-space: nowrap;
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
        }

        .macos-dock-item:hover .macos-dock-label {
          opacity: 1;
          transform: translateY(0);
        }

        .macos-dock-divider {
          width: 1px;
          height: 48px;
          background: rgba(0, 0, 0, 0.2);
          margin: 0 6px;
          align-self: center;
        }

        .macos-dock-reflection {
          width: 90%;
          height: 20px;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.15) 0%,
            transparent 100%
          );
          border-radius: 0 0 20px 20px;
          transform: scaleY(-1);
          margin-top: -2px;
          opacity: 0.5;
        }
      `}</style>
    </div>
  );
});

// ============================================================================
// DOCK ICONS - Classic Mac OS style mini figures
// ============================================================================

function FinderIcon() {
  // Classic Finder face icon - two-toned happy face
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Face background - left half blue, right half lighter */}
      <rect x="4" y="4" width="12" height="24" rx="2" fill="#4A90D9" />
      <rect x="16" y="4" width="12" height="24" rx="2" fill="#87CEEB" />
      {/* Eyes */}
      <ellipse cx="11" cy="13" rx="2.5" ry="3" fill="white" />
      <ellipse cx="21" cy="13" rx="2.5" ry="3" fill="white" />
      <circle cx="11" cy="13" r="1.5" fill="#333" />
      <circle cx="21" cy="13" r="1.5" fill="#333" />
      {/* Smile */}
      <path d="M10 20 Q16 25 22 20" stroke="#333" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Nose line */}
      <line x1="16" y1="14" x2="16" y2="18" stroke="#333" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function NotesIcon() {
  // Classic yellow lined notepad
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Paper shadow */}
      <rect x="7" y="5" width="20" height="24" rx="1" fill="#C4A000" />
      {/* Paper */}
      <rect x="5" y="3" width="20" height="24" rx="1" fill="#FFE066" />
      {/* Fold corner */}
      <path d="M20 3 L25 3 L25 8 Z" fill="#C4A000" />
      <path d="M20 3 L20 8 L25 8" fill="#E6C800" />
      {/* Lines */}
      <line x1="8" y1="10" x2="18" y2="10" stroke="#D4A" strokeWidth="1" opacity="0.4" />
      <line x1="8" y1="14" x2="20" y2="14" stroke="#D4A" strokeWidth="1" opacity="0.4" />
      <line x1="8" y1="18" x2="17" y2="18" stroke="#D4A" strokeWidth="1" opacity="0.4" />
      <line x1="8" y1="22" x2="19" y2="22" stroke="#D4A" strokeWidth="1" opacity="0.4" />
    </svg>
  );
}

function TextEditIcon() {
  // Classic pencil on paper
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Paper */}
      <rect x="4" y="6" width="18" height="22" rx="1" fill="white" stroke="#999" strokeWidth="1" />
      {/* Paper lines */}
      <line x1="7" y1="12" x2="19" y2="12" stroke="#DDD" strokeWidth="1" />
      <line x1="7" y1="16" x2="19" y2="16" stroke="#DDD" strokeWidth="1" />
      <line x1="7" y1="20" x2="16" y2="20" stroke="#DDD" strokeWidth="1" />
      {/* Pencil body */}
      <rect x="18" y="2" width="6" height="20" rx="1" fill="#FFD700" transform="rotate(30 21 12)" />
      {/* Pencil tip */}
      <polygon points="26,22 28,28 24,28" fill="#FFAA00" transform="rotate(30 26 25)" />
      <polygon points="26.5,26 28,28 25,28" fill="#333" transform="rotate(30 26 27)" />
      {/* Pencil eraser */}
      <rect x="18" y="1" width="6" height="3" rx="1" fill="#FF6B6B" transform="rotate(30 21 2.5)" />
      {/* Pencil band */}
      <rect x="18" y="4" width="6" height="2" fill="#888" transform="rotate(30 21 5)" />
    </svg>
  );
}

function MessagesIcon() {
  // Classic green speech bubble with dots
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Shadow */}
      <ellipse cx="17" cy="17" rx="12" ry="10" fill="#2D8C2D" />
      {/* Main bubble */}
      <ellipse cx="16" cy="15" rx="12" ry="10" fill="#34C759" />
      {/* Highlight */}
      <ellipse cx="14" cy="11" rx="8" ry="5" fill="#5DD879" opacity="0.6" />
      {/* Tail */}
      <path d="M8 22 Q4 28 2 28 Q6 26 10 22" fill="#34C759" />
      {/* Typing dots */}
      <circle cx="10" cy="15" r="2" fill="white" />
      <circle cx="16" cy="15" r="2" fill="white" />
      <circle cx="22" cy="15" r="2" fill="white" />
    </svg>
  );
}

function ITunesIcon() {
  // Classic iTunes music note with colorful background
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Background gradient circle */}
      <defs>
        <linearGradient id="itunesGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF2D55" />
          <stop offset="50%" stopColor="#FF6B9D" />
          <stop offset="100%" stopColor="#FF2D55" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="14" fill="url(#itunesGrad)" />
      {/* Inner glow */}
      <circle cx="14" cy="12" r="8" fill="white" opacity="0.2" />
      {/* Music notes */}
      <ellipse cx="10" cy="22" rx="4" ry="3" fill="white" />
      <ellipse cx="22" cy="19" rx="4" ry="3" fill="white" />
      {/* Note stems */}
      <rect x="13" y="8" width="2" height="14" fill="white" />
      <rect x="25" y="5" width="2" height="14" fill="white" />
      {/* Connecting beam */}
      <path d="M13 8 L27 5 L27 9 L13 12 Z" fill="white" />
    </svg>
  );
}

function TrashIcon() {
  // Classic mesh trash can
  return (
    <svg viewBox="0 0 32 32" fill="none">
      {/* Trash can body */}
      <path d="M7 10 L9 28 L23 28 L25 10 Z" fill="#8E8E93" />
      {/* Mesh lines - vertical */}
      <line x1="11" y1="12" x2="10" y2="26" stroke="#6E6E73" strokeWidth="1" />
      <line x1="16" y1="12" x2="16" y2="26" stroke="#6E6E73" strokeWidth="1" />
      <line x1="21" y1="12" x2="22" y2="26" stroke="#6E6E73" strokeWidth="1" />
      {/* Mesh lines - horizontal */}
      <line x1="8" y1="15" x2="24" y2="15" stroke="#6E6E73" strokeWidth="1" />
      <line x1="8" y1="20" x2="24" y2="20" stroke="#6E6E73" strokeWidth="1" />
      <line x1="9" y1="25" x2="23" y2="25" stroke="#6E6E73" strokeWidth="1" />
      {/* Lid */}
      <rect x="5" y="7" width="22" height="3" rx="1" fill="#A0A0A5" />
      {/* Handle */}
      <rect x="13" y="4" width="6" height="3" rx="1" fill="#A0A0A5" />
      {/* Highlight */}
      <rect x="6" y="7" width="20" height="1" fill="#C0C0C5" />
    </svg>
  );
}
